import { defineStore } from 'pinia';
// import { router } from '@src/router/index';
import { useLocalStorage } from '@src/utils/storage';
import { useStoreCity } from '@src/store/modules/city';
import { useStoreMapSettings } from '@src/store/modules/mapSettings';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore useMapVersion.js has no declaration file
import { resetMapVersionModule } from '@src/views/Index/hooks/useMapVersion';
import { useStoreSurfaceBase } from '@src/store/modules/surfaceBase';
import { getInfo, logout as logoutApi, type AuthInfoUser } from '@src/api/auth';
import { getCookie, isDevMode } from '@src/utils';
import { isFromCrmFamily, isFromCrmMobile } from '@src/utils/subApp';

export type UserInfo = AuthInfoUser;

export const useStoreUser = defineStore('user', () => {
    const storeCity = useStoreCity();
    const storeMapSettings = useStoreMapSettings();
    const storeSurfaceBase = useStoreSurfaceBase();

    const auth = useLocalStorage('auth', {
        token: '',
    });
    const user = ref<Partial<UserInfo>>({});
    const permissions = ref<string[]>([]);

    const setAuth = ({ token }: { token: string }) => {
        if (token && token !== auth.value.token) auth.value.token = token;
    };

    /** 生产环境从 cookie 恢复 token（开发环境走 URL query） */
    const bootstrapAuth = () => {
        if (isDevMode()) return;
        const token = getCookie('token');
        if (token) setAuth({ token });
    };

    const getUser = async () => {
        const { user: userData, permissions: permissionList } = await getInfo();
        user.value = userData;
        permissions.value = permissionList ?? [];
    };

    const hasPermission = (key: string) => permissions.value.includes('*:*:*') || permissions.value.includes(key);

    const logout = async ({ useApiLogout = true } = {}) => {
        try {
            /* 接入微观E家：子应用退出登录时、通过参数传递通知父应用，由父应用操作登出事件 */
            if (isFromCrmFamily()) return (window as any).ipcRenderer?.send('subApp:logout', { subAppName: 'LITE_MAP' });
            /* 接入微观世界：子应用退出登录时、通过参数传递通知父应用，由父应用操作登出事件 */
            if (isFromCrmMobile()) return window.parent.postMessage({ type: 'subApp.logout' }, '*');

            if (useApiLogout && auth.value.token) {
                await logoutApi().catch(() => {});
            }
        } finally {
            // 清空用户认证信息
            auth.value = {
                token: '',
            };
            user.value = {};
            permissions.value = [];

            // 清空城市相关数据（mapDataParamsByUserId 与 IndexedDB 保留，按 userId 隔离）
            storeCity.resetCityState();
            storeMapSettings.resetMapSettings();
            resetMapVersionModule();
            storeSurfaceBase.resetSurfaceBaseState();

            // 延迟跳转到登录页
            setTimeout(() => {
                window.location.reload();
                // router.replace({ name: 'Login' });
            }, 800);
        }
    };

    const isSuperMan = () => user.value.superMan === 'Y';

    return {
        auth,
        user,
        permissions,
        setAuth,
        bootstrapAuth,
        getUser,
        hasPermission,
        logout,
        isSuperMan,
    };
});
